import { AuthRetryableFetchError, type Session, type SupabaseClient } from '@supabase/supabase-js';

export const SESSION_EXPIRED_MESSAGE = '登录已失效，请重新登录。你的方案和历史记录不会因此删除。';

export class LoginRequiredError extends Error {
  constructor() { super(SESSION_EXPIRED_MESSAGE); this.name = 'LoginRequiredError'; }
}

export function createSessionFetch(fetcher: typeof fetch = (...args) => fetch(...args)): typeof fetch {
  return async (input, init) => {
    const response = await fetcher(input, init);
    const url = new URL(typeof input === 'string' ? input : input instanceof URL ? input.href : input.url);
    // auth-js 2.112.3 otherwise treats a refresh rate limit as a terminal API
    // error and deletes an expired session. Let its bounded retry/backoff path
    // handle throttling, just as it already handles network and 5xx failures.
    if (response.status === 429 && url.pathname.endsWith('/auth/v1/token') && url.searchParams.get('grant_type') === 'refresh_token') {
      throw new AuthRetryableFetchError('登录验证请求较多，请稍后重试。', 429);
    }
    return response;
  };
}

// Do not classify an arbitrary 401, network failure or rate limit as logout.
export function isInvalidSessionError(error: unknown): boolean {
  if (error instanceof LoginRequiredError) return true;
  if (!error || typeof error !== 'object') return false;
  const value = error as { code?: string; name?: string; message?: string };
  if (value.name === 'AuthSessionMissingError') return true;
  if (['refresh_token_not_found', 'refresh_token_already_used', 'session_not_found', 'session_expired'].includes(value.code ?? '')) return true;
  // Older auth responses can omit code. Keep this fallback deliberately narrow.
  return /^Invalid Refresh Token: (Refresh Token Not Found|Already Used|Session Expired)\.?$/i.test(value.message ?? '');
}

export async function readSessionSafely(auth: Pick<SupabaseClient['auth'], 'getSession'>): Promise<Session | undefined> {
  try {
    // The SDK already refreshes near-expiry sessions and serializes refreshes.
    // It also removes terminally invalid credentials from its scoped storage.
    // Do not repeat refreshSession with a captured, potentially rotated token.
    const { data, error } = await auth.getSession();
    if (error) throw error;
    return data.session ?? undefined;
  } catch (error) {
    if (isInvalidSessionError(error)) throw new LoginRequiredError();
    throw new Error('暂时无法验证登录状态，请检查网络后重试。', { cause: error });
  }
}

// Invalidate synchronously on auth events; effect cleanup alone is too late
// when SIGNED_OUT and a failed bootstrap settle in the same microtask turn.
export class AuthBootGate {
  private revision = 0;
  begin(): number { return ++this.revision; }
  invalidate(): void { this.revision++; }
  isCurrent(ticket: number): boolean { return this.revision === ticket; }
}
